/**
 * Centralized API Configuration
 * Single source of truth for all API endpoints and service URLs
 * Automatically switches between development and production based on build mode
 */

// Base URLs from environment variables
export const API_CONFIG = {
  // ==========================================
  // ✅ PRIMARY: Gateway URLs (ALL traffic must go through gateway)
  // ==========================================
  GATEWAY_URL: import.meta.env.VITE_API_GATEWAY_URL || 'https://mvpkielis.kielis.com:8000',
  WS_GATEWAY_URL: import.meta.env.VITE_WS_GATEWAY_URL || 'wss://mvpkielis.kielis.com:8000',

  // ==========================================
  // ⚠️ DEPRECATED: Direct service URLs
  // @deprecated SECURITY: DO NOT USE - Bypasses gateway authentication and account isolation
  // All API calls MUST go through GATEWAY_URL for proper security enforcement
  // These are kept for backward compatibility only and will be removed in future versions
  // ==========================================
  /**
   * @deprecated Use GATEWAY_URL instead. Direct access bypasses RLS and account isolation.
   */
  ORCHESTRATOR_URL: import.meta.env.VITE_API_ORCHESTRATOR_URL || 'https://mvpkielis.kielis.com:8001',

  /**
   * @deprecated Use WS_GATEWAY_URL instead. Direct WebSocket access bypasses authentication.
   */
  WS_ORCHESTRATOR_URL: import.meta.env.VITE_WS_ORCHESTRATOR_URL || 'wss://mvpkielis.kielis.com:8001',

  /**
   * @deprecated Use GATEWAY_URL with /api/clients route. Direct access bypasses account isolation.
   */
  DATA_URL: import.meta.env.VITE_API_DATA_URL || 'https://mvpkielis.kielis.com:8030',

  // TASK-584: MARKET_URL removed — market-data merged into portfolio-service; use GATEWAY_URL /api/market.

  // Frontend URL (for OAuth callbacks, etc)
  FRONTEND_URL: import.meta.env.VITE_FRONTEND_URL || 'https://mvpkielis.kielis.com',

  // Feature flags
  ENABLE_EMAIL: import.meta.env.VITE_ENABLE_EMAIL === 'true',
  USE_MOCK_API: import.meta.env.VITE_USE_MOCK_API === 'true',
};

// API timeout settings
export const API_TIMEOUTS = {
  DEFAULT: 30000, // 30 seconds
  LONG_RUNNING: 120000, // 2 minutes for operations like document processing
  AGENT_CALL: 60000, // 1 minute for agent calls
  UPLOAD: 300000, // 5 minutes for file uploads
};

// Feature flags
export const FEATURES = {
  USE_MOCK_API: API_CONFIG.USE_MOCK_API,
  ENABLE_EMAIL: API_CONFIG.ENABLE_EMAIL,
  // Safe-on: the engine now analyzes real proposals (TASK-517). Env-overridable
  // (set VITE_ENABLE_FINSA_COMPLIANCE=false to hide the trigger); defaults on.
  ENABLE_FINSA_COMPLIANCE: import.meta.env.VITE_ENABLE_FINSA_COMPLIANCE !== 'false',
  ENABLE_VOICE_COMMANDS: false,
  // TASK-428: cockpit feature-registry pattern. Defaults OFF during migration
  // (legacy hardcoded cockpit path renders when false). Flip the default to
  // `!== 'false'` once all phases have landed and been validated.
  ENABLE_FEATURE_REGISTRY: import.meta.env.VITE_ENABLE_FEATURE_REGISTRY === 'true',
  // TASK-769: the copilot thread follows the WORK (a delegated task) instead of
  // the route, so the conversation that produced a document is reachable from
  // it. Safe-on with a kill switch (VITE_ENABLE_TASK_THREADS=false) — the
  // cross-client isolation this re-keying could threaten is structural (a task
  // id belongs to one task, which belongs to one client) and asserted by spec,
  // so the flag exists to retire the behaviour fast if the field disagrees,
  // not to keep it dark.
  ENABLE_TASK_THREADS: import.meta.env.VITE_ENABLE_TASK_THREADS !== 'false',
};

// Helper function to get the appropriate base URL for a service
/**
 * @deprecated Use httpClient from lib/httpClient.ts instead
 * Direct service URLs bypass gateway authentication and account isolation
 */
export function getServiceUrl(service: 'gateway' | 'orchestrator' | 'data' | 'market'): string {
  // Log warning for non-gateway usage
  if (service !== 'gateway') {
    console.warn(
      `⚠️ SECURITY WARNING: getServiceUrl('${service}') bypasses gateway authentication. Use httpClient instead.`
    );
  }

  switch (service) {
    case 'gateway':
      return API_CONFIG.GATEWAY_URL;
    case 'orchestrator':
      console.error('🚨 DEPRECATED: orchestrator URL bypasses account isolation. Use gateway route instead.');
      return API_CONFIG.GATEWAY_URL; // Force gateway
    case 'data':
      console.error('🚨 DEPRECATED: data service URL bypasses account isolation. Use /api/clients via gateway.');
      return API_CONFIG.GATEWAY_URL; // Force gateway
    case 'market':
      console.error('🚨 DEPRECATED: market service URL bypasses authentication. Use /api/market via gateway.');
      return API_CONFIG.GATEWAY_URL; // Force gateway
    default:
      return API_CONFIG.GATEWAY_URL;
  }
}

// Helper function to get WebSocket URL
/**
 * Get WebSocket URL - always use gateway for account isolation and JWT authentication
 * ✅ TASK-083: Gateway WebSocket proxy is now active
 * @param service - Service type (always returns gateway for security)
 */
export function getWebSocketUrl(service: 'gateway' | 'orchestrator' = 'gateway'): string {
  if (service === 'orchestrator') {
    console.warn('⚠️ SECURITY: Direct orchestrator WebSocket bypasses authentication. Using gateway instead.');
  }
  // ✅ TASK-083: Always use gateway for WebSocket to enforce account isolation
  return API_CONFIG.WS_GATEWAY_URL;
}

// Export for backward compatibility with existing code
/**
 * @deprecated Use API_CONFIG.GATEWAY_URL directly
 */
export const API_BASE_URL = API_CONFIG.GATEWAY_URL;

/**
 * @deprecated Use API_CONFIG.WS_GATEWAY_URL directly
 * ✅ TASK-083: Now uses gateway for account isolation and authentication
 */
export const WS_BASE_URL = API_CONFIG.WS_GATEWAY_URL;

// Agent service URLs (these typically go through gateway in production)
export const AGENT_URLS = {
  NOTE_AGENT: `${API_CONFIG.GATEWAY_URL}/agents/note`,
  EMAIL_AGENT: `${API_CONFIG.GATEWAY_URL}/agents/email`,
  DOCUMENT_AGENT: `${API_CONFIG.GATEWAY_URL}/agents/document`,
  PORTFOLIO_AGENT: `${API_CONFIG.GATEWAY_URL}/agents/portfolio`,
};